/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */
import {
    AccessIssueEntryMethodLabel,
    AccessIssueProblemCase,
    AccessIssueProblemSet,
    AccessIssueSolutionLabel,
} from '@acp/shared';
import {
    assertEvent,
    assign,
    raise,
    setup,
} from 'xstate';
import { produce } from 'immer';
import { accessIssuesWizardActors } from './actors';

type AccessIssuesWizardEvents = { type: 'OPEN'; defaultEntryMethod?: AccessIssueEntryMethodLabel; enableDebugMode: boolean }
| { type: 'CLOSE_MODAL' }
| { type: 'FORCE_CLOSE_MODAL' }
| { type: 'HIDE_CLOSE_CONFIRMATION' }
| { type: 'CONFIRM_CLOSE' }
| { type: 'SKIP_PROBLEM_SET' }
| { type: 'NEXT_PROBLEM_SET' }
| { type: 'SELECT_SOLUTION'; idx: number }
| { type: 'SELECT_ENTRY_METHOD'; entryMethod: AccessIssueEntryMethodLabel }
| { type: 'UNBLOCK_OTHER_ENTRY_METHOD' }
| { type: 'FAKE_MARK_AS_AGENCY_ACTION'; solutionLabel: AccessIssueSolutionLabel; notes?: string; date?: Date }
| { type: 'MARK_AS_AGENCY_ACTION'; solutionLabel: AccessIssueSolutionLabel; notes?: string; date?: Date }
| { type: 'FATAL_ERROR'; message: string }
| { type: 'DISABLE_DEBUG_MODE' };

const accessIssuesWizardMachine = setup({
    types: {
        events: {} as AccessIssuesWizardEvents,
        context: {} as {
            propertyId: number;
            caseDetails: AccessIssueProblemCase['problem_case_details'] | null;
            defaultEntryMethod: AccessIssueEntryMethodLabel | null;

            blockedEntryMethodQueues: AccessIssueProblemCase['blocked_entry_methods'];

            entryMethods: {
                current: AccessIssueEntryMethodLabel | null;
                resolved: AccessIssueEntryMethodLabel[];
                available: AccessIssueEntryMethodLabel[];
            };

            problemSet: {
                current: AccessIssueProblemSet | null;
                queue: AccessIssueProblemSet[];
                resolvedCount: number;
                totalCount: number;
            };

            performedSolutions: AccessIssueSolutionLabel[];
            selectedSolutionIdx: number;
            fatalErrorMessage: string | null;
            showCloseConfirmation: boolean;
            enableDebugMode: boolean;
        },
        input: {} as {
            propertyId: number;
        },
    },
    actors: accessIssuesWizardActors,
    actions: {
        // Callback for React to know when to invalidate all sorts of access issues info
        onSolutionPerformed: (_, _params: {
            solutionLabel: AccessIssueSolutionLabel;
            entryMethod: AccessIssueEntryMethodLabel;
            resolvedEntryMethods: AccessIssueEntryMethodLabel[];
        }) => {},
        onEntryMethodUnblocked: (_, _params: {
            propertyId: number;
            entryMethod: AccessIssueEntryMethodLabel;
            resolvedEntryMethods: AccessIssueEntryMethodLabel[];
        }) => {},
        onNoAccessIssues: () => {},
        onModalOpened: () => {},
        onResolutionStarted: (_, _params: {
            entryMethod: AccessIssueEntryMethodLabel;
            resolvedEntryMethods: AccessIssueEntryMethodLabel[];
        }) => {},
        onModalClosed: () => {},
        onFatalError: (_, _params: { message: string }) => {},
        onNoSolutionsPresented: () => {},
        onAccessIssuePresented: () => {},
        onAllEntryMethodsDisqualified: () => {},

        // Called once we have received access issue data from the API
        setupWizardFromAccessIssues: assign((
            { context },
            params: { problemCase: AccessIssueProblemCase }
        ) => produce(context, (draft) => {
            draft.caseDetails = params.problemCase.problem_case_details;
            draft.blockedEntryMethodQueues = params.problemCase.blocked_entry_methods;

            // Reset entry method params
            draft.entryMethods.current = null;
            draft.entryMethods.resolved = [];
            draft.entryMethods.available = params.problemCase.blocked_entry_method_keys;

            // Reset any current problem sets
            draft.problemSet.current = null;
            draft.problemSet.queue = [];
            draft.problemSet.resolvedCount = 0;
            draft.problemSet.totalCount = 0;

            // Reset any performed solutions and which solution we're in
            draft.performedSolutions = [];
            draft.selectedSolutionIdx = 0;
            draft.fatalErrorMessage = null;
            draft.showCloseConfirmation = false;
        })),

        // Called when we want to move to the next item in the problem set queue
        internalResolveIssue: assign(({ context }) => produce(context, (draft) => {
            if (draft.problemSet.queue.length === 0) return;

            draft.problemSet.current = draft.problemSet.queue.shift()!;
            draft.problemSet.resolvedCount += 1;
            draft.selectedSolutionIdx = 0;
        })),

        // Called when a solution is succesfully performed
        internalPerformSolution: assign((
            { context },
            params: { solutionLabel: AccessIssueSolutionLabel }
        ) => produce(context, (draft) => {
            draft.performedSolutions.push(params.solutionLabel);

            // Internally remove any problem sets from the queue
            // that also include this solution
            // as it is guaranteed to be resolved now
            draft.problemSet.queue = draft.problemSet.queue.filter(
                (problemSet) => !problemSet.suggested_actions.some(
                    (action) => action.solution.solution_label === params.solutionLabel
                )
            );

            // Check if performing this solution completely resolves any other available
            // blocked entry methods
            // This will also resolve the current entry method if we've resolved all problem sets
            context.entryMethods.available.forEach((entryMethod) => {
                const blockedEntryMethod = context.blockedEntryMethodQueues[entryMethod];

                if (blockedEntryMethod.problem_sets.every(
                    (problemSet) => problemSet.suggested_actions.some(
                        (action) => draft.performedSolutions.includes(
                            action.solution.solution_label
                        )
                    ) // No suggested actions for the problem set
                        || problemSet.suggested_actions.length === 0
                )) {
                    draft.entryMethods.resolved.push(entryMethod);
                    draft.entryMethods.available = draft.entryMethods.available.filter(
                        (em) => em !== entryMethod
                    );
                }
            });
        })),

        // Called when we want to start solving problems for a specific entry method
        startProblemSetQueue: assign((
            { context },
            params: { entryMethod: AccessIssueEntryMethodLabel }
        ) => produce(context, (draft) => {
            // Clear the default entry method once we start solving problems
            draft.defaultEntryMethod = null;
            draft.selectedSolutionIdx = 0;

            if (!context.entryMethods.available.includes(params.entryMethod)) {
                throw new Error(`Selected entry method ${params.entryMethod} is not blocked`);
            }

            draft.entryMethods.current = params.entryMethod;

            const blockedEntryMethod = context.blockedEntryMethodQueues[params.entryMethod];

            // We'll only show problem sets that don't have a solution that we've already performed
            const availableProblemSets = blockedEntryMethod.problem_sets.filter(
                (problemSet) => !context.performedSolutions.some(
                    (solution) => problemSet.suggested_actions.some(
                        (action) => action.solution.solution_label === solution
                    )
                )
            );

            draft.problemSet.queue = availableProblemSets;
            draft.problemSet.current = draft.problemSet.queue.shift()!;
            draft.problemSet.resolvedCount = 0;
            draft.problemSet.totalCount = availableProblemSets.length;
        })),

        // Called when we want to reset the problem set queue
        // and potentially move to another entry method
        resetProblemSetQueue: assign((
            { context }
        ) => produce(context, (draft) => {
            draft.entryMethods.current = null;

            draft.problemSet.current = null;
            draft.problemSet.queue = [];
            draft.problemSet.resolvedCount = 0;
            draft.problemSet.totalCount = 0;
        })),
        showCloseConfirmation: assign(({ context }) => produce(context, (draft) => {
            draft.showCloseConfirmation = true;
        })),
        hideCloseConfirmation: assign(({ context }) => produce(context, (draft) => {
            draft.showCloseConfirmation = false;
        })),
    },
    guards: {
        canChangeToOtherEntryMethod: ({ context }) => context.entryMethods.available.length > 1,
        emptyProblemSetQueue: ({ context }) => context.problemSet.queue.length === 0,
        hasDefaultEntryMethod: ({ context }) => context.defaultEntryMethod !== null
            && context.entryMethods.available.includes(context.defaultEntryMethod),
        hasNoSuggestedActions: (
            { context }
        ) => context.problemSet.current!.suggested_actions.length === 0,
    },
}).createMachine({
    id: 'accessIssuesWizard',
    initial: 'idle',
    context: ({ input }) => ({
        propertyId: input.propertyId,
        caseDetails: null,
        defaultEntryMethod: null,
        blockedEntryMethodQueues: {},
        entryMethods: {
            current: null,
            resolved: [],
            available: [],
        },
        problemSet: {
            current: null,
            queue: [],
            resolvedCount: 0,
            totalCount: 0,
        },
        performedSolutions: [],
        selectedSolutionIdx: 0,
        fatalErrorMessage: null,
        showCloseConfirmation: false,
        enableDebugMode: false,
    }),
    on: {
        FATAL_ERROR: {
            description: 'A fatal error occurred',
            actions: [assign(({ context, event }) => produce(context, (draft) => {
                assertEvent(event, 'FATAL_ERROR');
                draft.fatalErrorMessage = event.message;
            })), {
                type: 'onFatalError',
                params: ({ event }) => ({ message: event.message }),
            }],
            target: '.fatalError',
        },
        DISABLE_DEBUG_MODE: {
            actions: assign(({ context }) => produce(context, (draft) => {
                draft.enableDebugMode = false;
            })),
        },
    },
    states: {
        idle: {
            on: {
                OPEN: {
                    target: 'initialLoad',
                    actions: [assign(({ context, event }) => produce(context, (draft) => {
                        assertEvent(event, 'OPEN');

                        draft.defaultEntryMethod = event.defaultEntryMethod ?? null;
                        draft.enableDebugMode = event.enableDebugMode;
                    })), {
                        type: 'onModalOpened',
                    }],
                },
            },
        },
        initialLoad: {
            description: 'Retrieve access issues data via API',
            invoke: {
                src: 'getAccessIssuesByProperty',
                input: ({ context }) => ({
                    propertyId: context.propertyId,
                }),
                onDone: [
                    {
                        guard: ({ event }) => event.output !== null
                            && event.output.blocked_entry_method_keys.length > 0
                            && event.output.problem_case_details.portal_suppression_level === 0,
                        description: 'Access issues were successfully retrieved and there are active problems',
                        actions: {
                            type: 'setupWizardFromAccessIssues',
                            params: ({ event }) => ({ problemCase: event.output }),
                        },
                        target: 'selectEntryMethod',
                    },
                    {
                        guard: ({ event }) => event.output !== null
                            && event.output.all_entry_methods_disqualified
                            && event.output.problem_case_details.portal_suppression_level === 0,
                        description: 'Access issues were successfully retrieved, but all entry methods are disqualified',
                        actions: 'onAllEntryMethodsDisqualified',
                        target: 'allEntryMethodsDisqualified',
                    },
                    {
                        description: 'Access issues were successfully retrieved but has no active problems',
                        target: 'noAccessIssuesError',
                        actions: 'onNoAccessIssues',
                    },
                ],
                onError: {
                    actions: raise(({ event }) => ({ type: 'FATAL_ERROR', message: (event.error as Error).message })),
                },
            },
        },
        noAccessIssuesError: {
            on: {
                CLOSE_MODAL: 'idle',
            },
        },
        resolveIssue: {
            entry: {
                type: 'onAccessIssuePresented',
            },
            always: {
                target: 'resolveIssueNoSolutions',
                guard: 'hasNoSuggestedActions',
                description: 'No suggested actions for the current problem set',
            },
            on: {
                CLOSE_MODAL: {
                    actions: 'showCloseConfirmation',
                },
                FORCE_CLOSE_MODAL: {
                    target: 'idle',
                    actions: ['hideCloseConfirmation', 'onModalClosed'],
                },
                HIDE_CLOSE_CONFIRMATION: {
                    actions: 'hideCloseConfirmation',
                },
                MARK_AS_AGENCY_ACTION: {
                    target: 'resolveIssuePending',
                },
                FAKE_MARK_AS_AGENCY_ACTION: {
                    target: 'resolveIssueCompleted',
                    actions: [
                        {
                            type: 'internalPerformSolution',
                            params: ({ event }) => ({ solutionLabel: event.solutionLabel }),
                        },
                        {
                            type: 'onSolutionPerformed',
                            params: ({ event, context }) => ({
                                solutionLabel: event.solutionLabel,
                                entryMethod: context.entryMethods.current!,
                                resolvedEntryMethods: context.entryMethods.resolved,
                            }),
                        },
                    ],
                },
                SELECT_SOLUTION: {
                    actions: assign(({ context, event }) => produce(context, (draft) => {
                        assertEvent(event, 'SELECT_SOLUTION');

                        draft.selectedSolutionIdx = event.idx;
                    })),
                },
                UNBLOCK_OTHER_ENTRY_METHOD: {
                    target: 'selectEntryMethod',
                    actions: 'resetProblemSetQueue',
                    guard: 'canChangeToOtherEntryMethod',
                },
                SELECT_ENTRY_METHOD: {
                    target: 'resolveIssue',
                    actions: [{
                        type: 'startProblemSetQueue',
                        params: ({ event }) => ({ entryMethod: event.entryMethod }),
                    }, {
                        type: 'onResolutionStarted',
                        params: ({ context }) => ({
                            entryMethod: context.entryMethods.current!,
                            resolvedEntryMethods: context.entryMethods.resolved,
                        }),
                    }],
                },
            },
        },
        resolveIssueNoSolutions: {
            entry: 'onNoSolutionsPresented',
            on: {
                CLOSE_MODAL: [
                    {
                        guard: 'emptyProblemSetQueue',
                        description: 'No more problem sets in the queue',
                        target: 'idle',
                        actions: 'onModalClosed',
                    },
                    {
                        actions: 'showCloseConfirmation',
                        description: 'Still has problem sets in the queue',
                    },
                ],
                FORCE_CLOSE_MODAL: {
                    target: 'idle',
                    actions: ['hideCloseConfirmation', 'onModalClosed'],
                },
                HIDE_CLOSE_CONFIRMATION: {
                    actions: 'hideCloseConfirmation',
                },
                NEXT_PROBLEM_SET: {
                    actions: 'internalResolveIssue',
                    target: 'resolveIssue',
                },
                UNBLOCK_OTHER_ENTRY_METHOD: {
                    target: 'selectEntryMethod',
                    actions: 'resetProblemSetQueue',
                    guard: 'canChangeToOtherEntryMethod',
                },
                SELECT_ENTRY_METHOD: {
                    target: 'resolveIssue',
                    actions: [{
                        type: 'startProblemSetQueue',
                        params: ({ event }) => ({ entryMethod: event.entryMethod }),
                    }, {
                        type: 'onResolutionStarted',
                        params: ({ context }) => ({
                            entryMethod: context.entryMethods.current!,
                            resolvedEntryMethods: context.entryMethods.resolved,
                        }),
                    }],
                },
            },
        },
        resolveIssuePending: {
            description: 'POST the action to the DS API',
            invoke: {
                src: 'actionProblem',
                input: ({ context, event }) => {
                    assertEvent(event, 'MARK_AS_AGENCY_ACTION');

                    return {
                        propertyId: context.propertyId,
                        solutionLabel: event.solutionLabel,
                        notes: event.notes,
                        date: event.date,
                    };
                },
                onDone: {
                    target: 'resolveIssueCompleted',
                    actions: [
                        {
                            type: 'internalPerformSolution',
                            params: ({ event }) => ({ solutionLabel: event.output.solutionLabel }),
                        },
                        {
                            type: 'onSolutionPerformed',
                            params: ({ event, context }) => ({
                                solutionLabel: event.output.solutionLabel,
                                entryMethod: context.entryMethods.current!,
                                resolvedEntryMethods: context.entryMethods.resolved,
                            }),
                        },
                    ],
                },
                onError: {
                    actions: raise(({ event }) => ({ type: 'FATAL_ERROR', message: (event.error as Error).message })),
                },
            },
        },
        resolveIssueCompleted: {
            always: {
                guard: 'emptyProblemSetQueue',
                description: 'No more problem sets in the queue',
                actions: {
                    type: 'onEntryMethodUnblocked',
                    params: ({ context }) => ({
                        propertyId: context.propertyId,
                        entryMethod: context.entryMethods.current!,
                        resolvedEntryMethods: context.entryMethods.resolved,
                    }),
                },
            },
            on: {
                CLOSE_MODAL: [
                    {
                        guard: 'emptyProblemSetQueue',
                        description: 'No more problem sets in the queue',
                        target: 'idle',
                        actions: 'onModalClosed',
                    },
                    {
                        actions: 'showCloseConfirmation',
                        description: 'Still has problem sets in the queue',
                    },
                ],
                FORCE_CLOSE_MODAL: {
                    target: 'idle',
                    actions: ['hideCloseConfirmation', 'onModalClosed'],
                },
                HIDE_CLOSE_CONFIRMATION: {
                    actions: 'hideCloseConfirmation',
                },
                NEXT_PROBLEM_SET: {
                    actions: 'internalResolveIssue',
                    target: 'resolveIssue',
                },
                UNBLOCK_OTHER_ENTRY_METHOD: {
                    target: 'selectEntryMethod',
                    actions: 'resetProblemSetQueue',
                    guard: 'canChangeToOtherEntryMethod',
                },
                SELECT_ENTRY_METHOD: {
                    target: 'resolveIssue',
                    actions: [{
                        type: 'startProblemSetQueue',
                        params: ({ event }) => ({ entryMethod: event.entryMethod }),
                    }, {
                        type: 'onResolutionStarted',
                        params: ({ context }) => ({
                            entryMethod: context.entryMethods.current!,
                            resolvedEntryMethods: context.entryMethods.resolved,
                        }),
                    }],
                },
            },
        },
        selectEntryMethod: {
            always: [
                {
                    guard: 'hasDefaultEntryMethod',
                    actions: [{
                        type: 'startProblemSetQueue',
                        params: ({ context }) => ({ entryMethod: context.defaultEntryMethod! }),
                    }, {
                        type: 'onResolutionStarted',
                        params: ({ context }) => ({
                            entryMethod: context.entryMethods.current!,
                            resolvedEntryMethods: context.entryMethods.resolved,
                        }),
                    }],
                    target: 'resolveIssue',
                    description: 'A default entry method was provided',
                },
                {
                    guard: ({ context }) => context.entryMethods.resolved.length === 0
                        && context.entryMethods.available.length > 1
                        && Object.values(context.blockedEntryMethodQueues).filter(
                            (blockedEntryMethod) => !blockedEntryMethod.is_blocked_by_preferences
                        ).length === 1,
                    actions: [
                        {
                            type: 'startProblemSetQueue',
                            params: ({ context }) => ({
                                entryMethod: Object.values(context.blockedEntryMethodQueues).filter(
                                    (
                                        blockedEntryMethod
                                    ) => !blockedEntryMethod.is_blocked_by_preferences
                                )[0].blocked_entry_method,
                            }),
                        }, {
                            type: 'onResolutionStarted',
                            params: ({ context }) => ({
                                entryMethod: context.entryMethods.current!,
                                resolvedEntryMethods: context.entryMethods.resolved,
                            }),
                        }],
                    target: 'resolveIssue',
                    description: 'Only one entry method is not blocked by preferences',
                },
                {
                    guard: ({ context }) => context.entryMethods.available.length === 1
                        && context.entryMethods.resolved.length === 0,
                    actions: [{
                        type: 'startProblemSetQueue',
                        params: ({ context }) => ({
                            entryMethod: context.entryMethods.available[0],
                        }),
                    }, {
                        type: 'onResolutionStarted',
                        params: ({ context }) => ({
                            entryMethod: context.entryMethods.current!,
                            resolvedEntryMethods: context.entryMethods.resolved,
                        }),
                    }],
                    target: 'resolveIssue',
                    description: 'Only one entry method is available and none have been resolved',
                },
            ],
            on: {
                CLOSE_MODAL: {
                    target: 'idle',
                    actions: 'onModalClosed',
                },
                SELECT_ENTRY_METHOD: {
                    target: 'resolveIssue',
                    actions: [
                        {
                            type: 'startProblemSetQueue',
                            params: ({ event }) => ({ entryMethod: event.entryMethod }),
                        }, {
                            type: 'onResolutionStarted',
                            params: ({ context }) => ({
                                entryMethod: context.entryMethods.current!,
                                resolvedEntryMethods: context.entryMethods.resolved,
                            }),
                        },
                    ],
                },
            },
        },
        allEntryMethodsDisqualified: {
            on: {
                CLOSE_MODAL: {
                    target: 'idle',
                    actions: 'onModalClosed',
                },
            },
        },
        fatalError: {
            on: {
                CLOSE_MODAL: {
                    target: 'idle',
                    actions: 'onModalClosed',
                },
            },
        },
    },
});

export { accessIssuesWizardMachine, AccessIssuesWizardEvents };
